import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { CompositionService } from "../../../../services/CompositionService";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { DatabaseIcon } from "lucide-react";
import ReviewField from "./ReviewField";

interface Composition {
  id: number;
  name: string;
  description?: string;
  intent_group_name?: string;
  application_name?: string;
  intent_name?: string;
  base_type?: string;
  meta?: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  members?: Array<{
    dataset_id: number;
    dataset_name: string;
    origin_dataset_id: number;
    request_count: number;
    sampling_type: string;
    sampling_weight: number;
  }>;
}

interface ReviewCompositionCardProps {
  compositionId: string;
}

const getSourceName = (composition: Composition): React.ReactNode => {
  if (composition.base_type === "intent_group") {
    return composition.meta?.intent_name || "Intent Group";
  } else if (composition.base_type === "application_intent") {
    const appName = composition.meta?.application_name || "App";
    const intentName = composition.meta?.action_type || "Intent";
    return `${appName} / ${intentName}`;
  }
  return "Unknown";
};

const getTotalRequestCount = (composition: Composition): number => {
  if (!composition.members) return 0;
  return composition.members.reduce((total, member) => total + (member.request_count || 0), 0);
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-6 w-32" />
        </div>
      ))}
    </div>
  </div>
);

const ReviewCompositionCard: React.FC<ReviewCompositionCardProps> = ({ compositionId }) => {
  const { apiKey } = usePermissions();
  const [composition, setComposition] = useState<Composition | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (compositionId) {
      CompositionService.getComposition(
        compositionId,
        setIsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          if (data) setComposition(data);
        },
        (error: Error) => {
          console.error("Error fetching composition details:", error);
        }
      );
    }
  }, [compositionId, apiKey]);

  const renderContent = () => {
    if (isLoading) return <LoadingSkeleton />;

    if (!composition) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-muted-foreground">No composition selected</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ReviewField label="Name" value={composition.name} />
        <ReviewField label="Description" value={composition.description || "No description"} />
        <ReviewField label="Source" value={getSourceName(composition)} />
        <ReviewField label="Total Requests" value={getTotalRequestCount(composition).toLocaleString()} />
        <ReviewField label="Datasets" value={`${composition.members?.length || 0} datasets`} />
      </div>
    );
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
          <DatabaseIcon className="w-5 h-5 text-maitai-green" />
          Composition
        </CardTitle>
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
};

export default ReviewCompositionCard;
